// src/pages/public/Login.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Fade,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Slide,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

import { useAuthContext } from "../../contexts/AuthContext";

// -------------------- helpers --------------------
function onlyDigits(v) {
  return (v || "").replace(/\D/g, "");
}

function isEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
}

// Nome: primeira letra maiúscula (por palavra), e limpa espaços duplicados
function formatFullName(v) {
  const raw = (v || "").replace(/\s+/g, " "); // normaliza espaços repetidos

  const endsWithSpace = /\s$/.test(raw); // usuário acabou de apertar espaço?

  // permite letras (com acento), espaços, apóstrofo e hífen
  const cleaned = raw.replace(/[^A-Za-zÀ-ÿ\s'-]/g, "");

  const formatted = cleaned
    .trim() // só pra formatar as palavras
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

  // se o usuário digitou espaço no final, devolve com o espaço pra ele conseguir continuar
  return endsWithSpace ? `${formatted} ` : formatted;
}

// Telefone: aceita só números, limita 11 dígitos e formata
function formatPhoneBR11(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  return `(${d.slice(0, 2)})${d.slice(2)}`; // sem espaço / sem hífen, igual você pediu
}

// CPF: 000.000.000-00 (aceita só números e limita 11)
function formatCPF(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function isValidCPFFormat(v) {
  // apenas valida o "formato" e quantidade (não é o algoritmo do CPF)
  return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test((v || "").trim());
}

// Nascimento: dd/mm/aaaa (aceita só números e limita 8 dígitos)
function formatBirthBR(v) {
  const d = onlyDigits(v).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

function parseBirthBR(v) {
  const s = (v || "").trim();
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return null;

  const [dd, mm, yyyy] = s.split("/").map((x) => parseInt(x, 10));
  if (!dd || !mm || !yyyy) return null;

  // cria em meia-noite local
  const dt = new Date(yyyy, mm - 1, dd);
  // valida se a data existe mesmo (ex: 31/02)
  if (
    dt.getFullYear() !== yyyy ||
    dt.getMonth() !== mm - 1 ||
    dt.getDate() !== dd
  )
    return null;

  return dt;
}

function calcAge(birthDate, now = new Date()) {
  let age = now.getFullYear() - birthDate.getFullYear();
  const m = now.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birthDate.getDate())) age--;
  return age;
}

function passwordScore(pw) {
  const s = (pw || "").trim();
  let score = 0;
  if (s.length >= 8) score += 1;
  if (/[A-Z]/.test(s)) score += 1;
  if (/[a-z]/.test(s)) score += 1;
  if (/\d/.test(s)) score += 1;
  if (/[^A-Za-z0-9]/.test(s)) score += 1;
  return Math.min(score, 5);
}

function scoreLabel(score) {
  if (score <= 1) return "Fraca";
  if (score === 2) return "Ok";
  if (score === 3) return "Boa";
  if (score === 4) return "Forte";
  return "Muito forte";
}

// -------------------- component --------------------
/** Determina para onde redirecionar com base no role/approvalStatus do perfil */
function resolveRedirect(profile, fallback) {
  if (!profile) return fallback || "/";
  if (profile.role === "organizer") {
    if (profile.approvalStatus === "approved") return "/dashboard";
    if (profile.approvalStatus === "pending")
      return "/organizadores/solicitacao-enviada";
    // rejected: fica no site publico
    return "/";
  }
  // customer
  return fallback || "/";
}

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();

  const fromPath = location.state?.from?.pathname || null;

  const {
    signIn,
    signUp: registerCustomer,
    sendPasswordReset,
  } = useAuthContext();

  const [tab, setTab] = useState(0); // 0 = entrar, 1 = criar conta

  // login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPass, setShowPass] = useState(false);

  // register
  const [name, setName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [birth, setBirth] = useState(""); // ✅ NOVO
  const [regPass, setRegPass] = useState("");
  const [regPass2, setRegPass2] = useState("");
  const [showRegPass, setShowRegPass] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // ui
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ type: "info", msg: "" });

  const heroBg = `
    radial-gradient(1100px 520px at 12% 22%, rgba(26,255,122,0.16), transparent 60%),
    radial-gradient(900px 520px at 92% 8%, rgba(26,255,122,0.10), transparent 55%),
    linear-gradient(180deg, rgba(255,255,255,0.04), transparent)
  `;

  const cardSx = {
    borderRadius: 3,
    border: "1px solid var(--linha)",
    background: "rgba(255,255,255,0.04)",
    boxShadow: "0 18px 70px rgba(0,0,0,0.38)",
    backdropFilter: "blur(18px)",
    overflow: "hidden",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      background: "rgba(0,0,0,0.18)",
      "& fieldset": { borderColor: "var(--linha)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
      "&.Mui-focused fieldset": { borderColor: "rgba(26,255,122,0.70)" },
    },
    "& .MuiInputBase-input::placeholder": { opacity: 0.7 },
  };

  const passScore = useMemo(() => passwordScore(regPass), [regPass]);
  const setMsg = (type, msg) => setToast({ type, msg });

  useEffect(() => {
    if (location.state?.reason === "checkout_auth_required") {
      setToast({
        type: "info",
        msg: "Faça login ou crie sua conta para continuar para o checkout.",
      });
    }
  }, [location.state]);

  const validateLogin = () => {
    if (!isEmail(email))
      return (setMsg("error", "Digite um e-mail válido."), false);
    if (!password || password.length < 6)
      return (setMsg("error", "Senha inválida."), false);
    return true;
  };

  const validateRegister = () => {
    if ((name || "").trim().length < 2)
      return (setMsg("error", "Informe seu nome."), false);

    if (!isEmail(regEmail))
      return (setMsg("error", "Digite um e-mail válido."), false);

    // telefone obrigatório: 11 dígitos
    if (onlyDigits(phone).length !== 11)
      return (
        setMsg("error", "Telefone inválido. Use DDD + 9 dígitos (11 números)."),
        false
      );

    // CPF obrigatório: 11 dígitos + máscara correta
    if (!isValidCPFFormat(cpf) || onlyDigits(cpf).length !== 11)
      return (
        setMsg("error", "CPF inválido. Use o formato 000.000.000-00."),
        false
      );

    // nascimento obrigatório + >= 18
    const birthDate = parseBirthBR(birth);
    if (!birthDate)
      return (
        setMsg("error", "Data de nascimento inválida. Use 00/00/0000."),
        false
      );

    const age = calcAge(birthDate, new Date());
    if (age < 18)
      return (
        setMsg(
          "error",
          "Você precisa ter 18 anos ou mais para criar uma conta.",
        ),
        false
      );

    if (!regPass || regPass.length < 8)
      return (
        setMsg("error", "Sua senha precisa ter pelo menos 8 caracteres."),
        false
      );

    if (passScore < 3)
      return (
        setMsg(
          "warning",
          "Sugestão: deixe sua senha mais forte (maiúscula, número e símbolo).",
        ),
        false
      );

    if (regPass !== regPass2)
      return (setMsg("error", "As senhas não conferem."), false);

    if (!acceptTerms)
      return (setMsg("error", "Você precisa aceitar os Termos de Uso."), false);

    if (!acceptPrivacy)
      return (
        setMsg("error", "Você precisa aceitar a Política de Privacidade."),
        false
      );

    return true;
  };

  const handleLogin = async () => {
    setToast({ type: "info", msg: "" });
    if (!validateLogin()) return;

    setLoading(true);
    try {
      const { profile } = await signIn(email.trim(), password, { remember });
      const dest = resolveRedirect(profile, fromPath);
      nav(dest, { replace: true });
    } catch (e) {
      const code = e?.code || "";
      let msg = "Nao foi possivel entrar. Verifique seus dados.";
      if (
        code === "auth/user-not-found" ||
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential"
      ) {
        msg = "E-mail ou senha incorretos.";
      } else if (code === "auth/too-many-requests") {
        msg = "Muitas tentativas. Aguarde alguns minutos.";
      }
      setMsg("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setToast({ type: "info", msg: "" });
    if (!validateRegister()) return;

    setLoading(true);
    try {
      await registerCustomer({
        name: name.trim(),
        email: regEmail.trim(),
        phone: onlyDigits(phone),
        cpf: onlyDigits(cpf),
        birth,
        password: regPass,
        marketingOptIn,
      });

      setMsg(
        "success",
        "Conta criada! Agora voce ja pode comprar seus ingressos.",
      );
      nav(fromPath || "/", { replace: true });
    } catch (e) {
      const code = e?.code || "";
      let msg = "Nao foi possivel criar sua conta.";
      if (code === "auth/email-already-in-use") {
        msg = "Este e-mail ja esta cadastrado. Faca login.";
      }
      setMsg("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setToast({ type: "info", msg: "" });
    const targetEmail = (email || "").trim();
    if (!isEmail(targetEmail))
      return (setMsg("error", "Digite seu e-mail acima para recuperar."), null);

    setLoading(true);
    try {
      await sendPasswordReset(targetEmail);
      setMsg(
        "success",
        "Te enviamos um e-mail com instruções para redefinir a senha.",
      );
    } catch (e) {
      setMsg(
        "error",
        e?.message || "Não foi possível enviar o e-mail de recuperação.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", pb: { xs: 6, md: 8 } }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 6, md: 8 },
          pb: { xs: 4, md: 6 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: heroBg,
            pointerEvents: "none",
          }}
        />

        <Container
          maxWidth={false}
          sx={{ maxWidth: "1120px", px: { xs: 2, sm: 2.5 } }}
        >
          <Slide direction="up" in timeout={520}>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  letterSpacing: "0.14em",
                  fontSize: 12,
                  opacity: 0.8,
                  mb: 1,
                }}
              >
                CONTA ONURB PASS
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 950,
                  letterSpacing: "-0.03em",
                  fontSize: { xs: 34, sm: 42, md: 52 },
                  lineHeight: 1.05,
                  mb: 1.2,
                }}
              >
                Entrar ou criar conta
              </Typography>

              <Typography
                sx={{ maxWidth: "72ch", opacity: 0.78, lineHeight: 1.7 }}
              >
                Para comprar ingressos e acessar seus QR Codes com segurança.
                Nós <b>não armazenamos dados de cartão</b> — pagamentos são
                processados pelo provedor (InfinitePay).
              </Typography>
            </Box>
          </Slide>

          <Fade in timeout={700}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {/* CARD PRINCIPAL */}
              <Box sx={cardSx}>
                <Box
                  sx={{
                    px: { xs: 2, sm: 2.6 },
                    pt: { xs: 2, sm: 2.6 },
                    pb: 1.4,
                    borderBottom: "1px solid var(--linha)",
                    background:
                      "linear-gradient(180deg, rgba(26,255,122,0.08), rgba(255,255,255,0.02))",
                  }}
                >
                  <Tabs
                    value={tab}
                    onChange={(_, v) => {
                      setToast({ type: "info", msg: "" });
                      setTab(v);
                    }}
                    variant="fullWidth"
                    sx={{
                      "& .MuiTab-root": {
                        fontWeight: 950,
                        letterSpacing: "0.06em",
                      },
                      "& .MuiTabs-indicator": { height: 3 },
                    }}
                  >
                    <Tab label="ENTRAR" />
                    <Tab label="CRIAR CONTA" />
                  </Tabs>
                </Box>

                <Box sx={{ p: { xs: 2, sm: 2.6 } }}>
                  {toast.msg ? (
                    <Alert
                      severity={toast.type}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        background: "rgba(0,0,0,0.28)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      {toast.msg}
                    </Alert>
                  ) : null}

                  {tab === 0 ? (
                    <Stack spacing={1.4}>
                      <TextField
                        label="E-mail"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="voce@dominio.com"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ opacity: 0.85 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />

                      <TextField
                        label="Senha"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Sua senha"
                        type={showPass ? "text" : "password"}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon sx={{ opacity: 0.85 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPass((s) => !s)}
                                edge="end"
                              >
                                {showPass ? (
                                  <VisibilityOffRoundedIcon />
                                ) : (
                                  <VisibilityRoundedIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={0.5}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={remember}
                              onChange={(e) => setRemember(e.target.checked)}
                            />
                          }
                          label={
                            <Typography sx={{ fontWeight: 800, opacity: 0.9 }}>
                              Manter conectado
                            </Typography>
                          }
                        />

                        <Button
                          variant="text"
                          onClick={handleForgot}
                          disabled={loading}
                          sx={{ fontWeight: 900, opacity: 0.9 }}
                        >
                          Esqueci minha senha
                        </Button>
                      </Stack>

                      <Button
                        variant="contained"
                        onClick={handleLogin}
                        disabled={loading}
                        sx={{ py: 1.2, fontWeight: 950, boxShadow: "none" }}
                      >
                        {loading ? "Entrando..." : "Entrar"}
                      </Button>

                      <Divider
                        sx={{ opacity: 0.25, borderColor: "var(--linha)" }}
                      />

                      <Typography sx={{ opacity: 0.75, fontSize: 13 }}>
                        Não tem conta?{" "}
                        <Button
                          variant="text"
                          sx={{ fontWeight: 950 }}
                          onClick={() => setTab(1)}
                        >
                          Criar agora
                        </Button>
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={1.4}>
                      <TextField
                        label="Nome completo"
                        value={name}
                        onChange={(e) =>
                          setName(formatFullName(e.target.value))
                        }
                        placeholder="Seu nome"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineIcon sx={{ opacity: 0.85 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />

                      <TextField
                        label="E-mail"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        placeholder="voce@dominio.com"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ opacity: 0.85 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.2}
                      >
                        <TextField
                          label="Telefone"
                          value={phone}
                          onChange={(e) =>
                            setPhone(formatPhoneBR11(e.target.value))
                          }
                          placeholder="(11) 981726373"
                          fullWidth
                          inputProps={{ inputMode: "numeric" }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIphoneOutlinedIcon
                                  sx={{ opacity: 0.85 }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />

                        <TextField
                          label="CPF"
                          value={cpf}
                          onChange={(e) => setCpf(formatCPF(e.target.value))}
                          placeholder="000.000.000-00"
                          fullWidth
                          inputProps={{ inputMode: "numeric" }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BadgeOutlinedIcon sx={{ opacity: 0.85 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </Stack>

                      {/* ✅ NOVO: DATA DE NASCIMENTO */}
                      <TextField
                        label="Data de nascimento"
                        value={birth}
                        onChange={(e) =>
                          setBirth(formatBirthBR(e.target.value))
                        }
                        placeholder="00/00/0000"
                        fullWidth
                        inputProps={{ inputMode: "numeric" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthOutlinedIcon
                                sx={{ opacity: 0.85 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                        helperText={
                          <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                            Você precisa ter <b>18 anos</b> ou mais.
                          </Typography>
                        }
                      />

                      <TextField
                        label="Senha"
                        value={regPass}
                        onChange={(e) => setRegPass(e.target.value)}
                        placeholder="Crie uma senha forte"
                        type={showRegPass ? "text" : "password"}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon sx={{ opacity: 0.85 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowRegPass((s) => !s)}
                                edge="end"
                              >
                                {showRegPass ? (
                                  <VisibilityOffRoundedIcon />
                                ) : (
                                  <VisibilityRoundedIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                        helperText={
                          <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                            Força: <b>{scoreLabel(passScore)}</b> · mínimo 8
                            caracteres, com número e símbolo.
                          </Typography>
                        }
                      />

                      <TextField
                        label="Confirmar senha"
                        value={regPass2}
                        onChange={(e) => setRegPass2(e.target.value)}
                        placeholder="Repita a senha"
                        type={showRegPass ? "text" : "password"}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon sx={{ opacity: 0.85 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />

                      <Box
                        sx={{
                          p: 1.6,
                          borderRadius: 2,
                          border: "1px solid rgba(255,255,255,0.10)",
                          background: "rgba(0,0,0,0.18)",
                        }}
                      >
                        <Stack spacing={0.6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={acceptTerms}
                                onChange={(e) =>
                                  setAcceptTerms(e.target.checked)
                                }
                              />
                            }
                            label={
                              <Typography
                                sx={{ fontWeight: 850, opacity: 0.92 }}
                              >
                                Eu aceito os{" "}
                                <Typography
                                  component={RouterLink}
                                  to="/termos"
                                  sx={{
                                    fontWeight: 950,
                                    textDecoration: "underline",
                                  }}
                                >
                                  Termos de Uso
                                </Typography>
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={acceptPrivacy}
                                onChange={(e) =>
                                  setAcceptPrivacy(e.target.checked)
                                }
                              />
                            }
                            label={
                              <Typography
                                sx={{ fontWeight: 850, opacity: 0.92 }}
                              >
                                Eu aceito a{" "}
                                <Typography
                                  component={RouterLink}
                                  to="/privacidade"
                                  sx={{
                                    fontWeight: 950,
                                    textDecoration: "underline",
                                  }}
                                >
                                  Política de Privacidade
                                </Typography>
                              </Typography>
                            }
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={marketingOptIn}
                                onChange={(e) =>
                                  setMarketingOptIn(e.target.checked)
                                }
                              />
                            }
                            label={
                              <Typography sx={{ opacity: 0.78 }}>
                                Quero receber novidades e ofertas (opcional)
                              </Typography>
                            }
                          />
                        </Stack>
                      </Box>

                      <Button
                        variant="contained"
                        onClick={handleRegister}
                        disabled={loading}
                        sx={{ py: 1.2, fontWeight: 950, boxShadow: "none" }}
                      >
                        {loading ? "Criando..." : "Criar conta"}
                      </Button>

                      <Divider
                        sx={{ opacity: 0.25, borderColor: "var(--linha)" }}
                      />

                      <Typography sx={{ opacity: 0.75, fontSize: 13 }}>
                        Já tem conta?{" "}
                        <Button
                          variant="text"
                          sx={{ fontWeight: 950 }}
                          onClick={() => setTab(0)}
                        >
                          Entrar
                        </Button>
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>

              {/* SIDE TRUST / SECURITY */}
              <Box sx={{ ...cardSx, p: { xs: 2, sm: 2.6 } }}>
                <Typography sx={{ fontWeight: 950, fontSize: 16, mb: 1 }}>
                  Segurança e transparência
                </Typography>

                <Typography sx={{ opacity: 0.78, lineHeight: 1.75, mb: 2 }}>
                  Sua conta protege seus ingressos e QR Codes. Dados sensíveis
                  são tratados com cuidado: senha é protegida, e pagamentos são
                  processados pelo provedor.
                </Typography>

                <Stack spacing={1.2}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.2,
                      alignItems: "center",
                      p: 1.4,
                      borderRadius: 2,
                      border: "1px solid rgba(255,255,255,0.10)",
                      background: "rgba(0,0,0,0.18)",
                    }}
                  >
                    <VerifiedUserOutlinedIcon sx={{ opacity: 0.9 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 950, fontSize: 14 }}>
                        Acesso seguro
                      </Typography>
                      <Typography sx={{ opacity: 0.75, fontSize: 12 }}>
                        Recomendamos senha forte e e-mail válido.
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.2,
                      alignItems: "center",
                      p: 1.4,
                      borderRadius: 2,
                      border: "1px solid rgba(255,255,255,0.10)",
                      background: "rgba(0,0,0,0.18)",
                    }}
                  >
                    <SecurityOutlinedIcon sx={{ opacity: 0.9 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 950, fontSize: 14 }}>
                        Dados sensíveis
                      </Typography>
                      <Typography sx={{ opacity: 0.75, fontSize: 12 }}>
                        Não guardamos dados de cartão no front-end.
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 1.6,
                      borderRadius: 2,
                      border: "1px solid rgba(26,255,122,0.22)",
                      background: "rgba(26,255,122,0.08)",
                    }}
                  >
                    <Typography sx={{ fontWeight: 950, fontSize: 14, mb: 0.4 }}>
                      Dica rápida
                    </Typography>
                    <Typography
                      sx={{ opacity: 0.85, fontSize: 13, lineHeight: 1.7 }}
                    >
                      Use uma senha única com pelo menos 8 caracteres, número e
                      símbolo. Se possível, ative autenticação adicional quando
                      ela estiver disponível.
                    </Typography>
                  </Box>

                  <Typography sx={{ opacity: 0.65, fontSize: 12, mt: 0.5 }}>
                    Ao continuar, você concorda com os Termos e a Política de
                    Privacidade.
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
}
